import { Box, Chip, Stack, TextField, MenuItem, Typography, Avatar, AvatarGroup, Tooltip } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteAlert from "../../../../components/Alert/DeleteAlert";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import {
    setContentCurrentPage,
    setContentPageLimit,
    setContentTotalCount,
    setContentTotalPages,
} from "../../../../lib/slices/pagination/contentPageSlice";
import { deleteContent, getAllContent } from "../../../../services/contentApiFn";
import { handleOpenSnackBar } from "../../../../utils";

const headerData = [
    "productName",
    "status",
    "channels",
    "createdAt",
    "actions",
];

const tableRows = [
    "product_name",
    "status_chip",
    "channels_display",
    "created_date",
    "actions",
];

const getStatusChip = (status) => {
    switch (status?.toLowerCase()) {
        case 'draft': return <Chip label="Draft" size="small" />;
        case 'approved': return <Chip label="Approved" size="small" color="primary" />;
        case 'scheduled': return <Chip label="Scheduled" size="small" color="warning" />;
        case 'published': return <Chip label="Published" size="small" color="success" />;
        case 'failed': return <Chip label="Failed" size="small" color="error" />;
        default: return <Chip label={status || 'Unknown'} size="small" />;
    }
};

const getProductName = (product) => {
    if (!product) return "N/A";
    if (typeof product === "string") return product;
    if (typeof product.name === "string") return product.name;
    if (typeof product.name === "object" && product.name) return product.name.en || product.name.ro || "N/A";
    return "N/A";
};

const ALLOWED_CHANNELS = ['blog', 'facebook', 'instagram', 'email'];

const getChannelsDisplay = (channels) => {
    let channelList = [];
    if (Array.isArray(channels)) {
        channelList = channels.filter(ch => ALLOWED_CHANNELS.includes(ch?.toLowerCase()));
    } else if (channels && typeof channels === 'object') {
        channelList = ALLOWED_CHANNELS.filter(k => channels[k] && typeof channels[k] === 'object' && Object.keys(channels[k]).length > 0);
    }
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {channelList.map((ch, i) => (
                <Chip key={i} label={ch} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            ))}
        </Stack>
    );
};

function ContentList({ handleChangeTab }) {
    const openDeleteDialog = useRef(null);
    const fetchIdRef = useRef(0);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [allContent, setAllContent] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");

    // Read pagination state safely, or fallback to local if reducer not added to store yet
    const pagination = useSelector((state) => state.contentPaginationReducer);

    const fetchAllContentData = async (page, limit, status, search) => {
        const fetchId = ++fetchIdRef.current;
        try {
            setIsLoading(true);
            const params = { page, limit };
            if (status && status !== "All") params.status = status.toLowerCase();
            if (search) params.search = search;

            const response = await getAllContent(params);
            if (fetchId !== fetchIdRef.current) return;
            
            if (response?.data?.status === "success") {
                const contentData = response?.data?.data?.data || (Array.isArray(response?.data?.data) ? response.data.data : []);
                const paginationData = response?.data?.data?.pagination || response?.data?.data || {};
                
                const formattedData = Array.isArray(contentData) ? contentData.map(item => ({
                    ...item,
                    product_name: getProductName(item.product) || item.productName || "N/A",
                    status_chip: getStatusChip(item.status),
                    channels_display: getChannelsDisplay(item.channels),
                    created_date: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A",
                })) : [];

                setAllContent(formattedData);
                dispatch(setContentPageLimit(paginationData.limit || limit));
                dispatch(setContentCurrentPage(paginationData.page || page));
                dispatch(setContentTotalCount(paginationData.total ?? formattedData.length));
                dispatch(setContentTotalPages(paginationData.totalPages || Math.ceil((paginationData.total || formattedData.length) / (paginationData.limit || limit)) || 1));
            } else {
                setAllContent([]);
            }
        } catch (error) {
            if (fetchId !== fetchIdRef.current) return;
            console.error("Error fetching content list:", error);
            setAllContent([]);
        } finally {
            if (fetchId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAllContentData(pagination.page, pagination.limit, statusFilter, searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [pagination.page, pagination.limit, statusFilter, searchQuery]);

    const handlePageChange = (event, newPage) => {
        dispatch(setContentCurrentPage(newPage + 1));
    };

    const handleRowsPerPageChange = (event) => {
        const limit = parseInt(event.target.value, 10);
        dispatch(setContentPageLimit(limit));
        dispatch(setContentCurrentPage(1));
    };

    const handleOpenDeleteDialog = (params) => {
        openDeleteDialog.current.openDialog({
            data: params.row,
            warningText: "Are you sure you want to delete this content?",
        });
    };

    const handleDelete = async (params) => {
        try {
            setIsLoading(true);
            const response = await deleteContent(params.data._id);
            if (response?.data?.status === "success") {
                openDeleteDialog.current.closeDialog();
                fetchAllContentData(pagination.page, pagination.limit, statusFilter, searchQuery);
                handleOpenSnackBar("success", "Content deleted successfully");
            } else {
                handleOpenSnackBar("error", response?.data?.message || "Failed to delete");
            }
        } catch (error) {
            handleOpenSnackBar("error", error?.message || "Failed to delete");
        } finally {
            setIsLoading(false);
        }
    };

    const renderHeaderAction = () => {
        return (
            <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                    size="small"
                    placeholder="Search product..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: 200, bgcolor: 'background.paper' }}
                />
                <TextField
                    select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ width: 150, bgcolor: 'background.paper' }}
                >
                    {["All", "Draft", "Approved", "Scheduled", "Published", "Failed"].map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <GlobalButton
                    btnText="Generate New"
                    handleClickBtn={() => handleChangeTab("generate_content")}
                />
            </Stack>
        );
    };

    const renderTable = useMemo(() => {
        return (
            <DynamicPaginatedTable
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allContent}
                showActions
                showEdit
                showDelete
                handleEdit={({ row }) => {
                    navigate(`/content-hub/${row?._id}`);
                }}
                handleDelete={handleOpenDeleteDialog}
                paginatedData={pagination}
                showDynamicPagination={true}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 30, 50, 100]}
                isLoading={isLoading}
            />
        );
    }, [allContent, isLoading, pagination]);

    return (
        <PaperContainer
            title="Content Hub"
            subtitle="Manage AI-generated marketing content"
            headerActions={renderHeaderAction()}
        >
            {renderTable}
            <DeleteAlert ref={openDeleteDialog} handleDelete={handleDelete} />
        </PaperContainer>
    );
}

export default ContentList;
